import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { PaginationMeta } from '@/api/pagination';

export interface PaginatorProps {
  pagination: PaginationMeta | undefined;
  onPageChange: (page: number) => void;
  /** Pages to keep on each side of the current page before collapsing into "…". */
  siblingCount?: number;
  className?: string;
}

/**
 * Universal numbered paginator used by every list page.
 *
 * Renders `‹ 1 2 … 5 6 7 … 12 ›` and calls `onPageChange` with the
 * one-indexed page number on click. Hides itself when there's only a single
 * page — there is no "disabled" state for the arrows; per the design system
 * we hide unavailable affordances rather than disabling them.
 */
export const Paginator: React.FC<PaginatorProps> = ({
  pagination,
  onPageChange,
  siblingCount = 1,
  className,
}) => {
  if (!pagination) return null;
  const { page, totalPages } = pagination;
  if (totalPages <= 1) return null;

  const pages = buildPageList(page, totalPages, siblingCount);

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex items-center justify-center gap-1', className)}
    >
      {page > 1 && (
        <PageButton
          aria-label="Previous page"
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4 stroke-[1.5]" />
        </PageButton>
      )}

      {pages.map((entry, idx) =>
        entry === 'gap' ? (
          <span
            key={`gap-${idx}`}
            className="px-2 text-sm text-muted-foreground select-none"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <PageButton
            key={entry}
            isActive={entry === page}
            aria-label={`Page ${entry}`}
            aria-current={entry === page ? 'page' : undefined}
            onClick={() => onPageChange(entry)}
          >
            {entry}
          </PageButton>
        ),
      )}

      {page < totalPages && (
        <PageButton
          aria-label="Next page"
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="h-4 w-4 stroke-[1.5]" />
        </PageButton>
      )}
    </nav>
  );
};

interface PageButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
}

const PageButton: React.FC<PageButtonProps> = ({
  isActive,
  className,
  children,
  ...rest
}) => (
  <button
    type="button"
    className={cn(
      'inline-flex h-9 min-w-9 items-center justify-center rounded-full px-3 text-sm font-medium transition-colors cursor-pointer',
      isActive
        ? 'bg-foreground text-background'
        : 'text-foreground hover:bg-foreground/5 active:bg-foreground/10',
      className,
    )}
    {...rest}
  >
    {children}
  </button>
);

type PageEntry = number | 'gap';

/**
 * Build the visible sequence of page tokens with sliding window + gaps.
 * Always shows first and last page; collapses long gaps as 'gap'.
 */
function buildPageList(
  current: number,
  total: number,
  siblingCount: number,
): PageEntry[] {
  const totalNumbers = siblingCount * 2 + 5; // first, last, current, 2*siblings, 2*gaps
  if (total <= totalNumbers) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(current - siblingCount, 1);
  const rightSibling = Math.min(current + siblingCount, total);

  const showLeftGap = leftSibling > 2;
  const showRightGap = rightSibling < total - 1;

  const result: PageEntry[] = [1];

  if (showLeftGap) {
    result.push('gap');
  } else {
    for (let i = 2; i < leftSibling; i++) result.push(i);
  }

  for (let i = leftSibling; i <= rightSibling; i++) {
    if (i !== 1 && i !== total) result.push(i);
  }

  if (showRightGap) {
    result.push('gap');
  } else {
    for (let i = rightSibling + 1; i < total; i++) result.push(i);
  }

  result.push(total);
  return result;
}
